import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Check, X, Clock, Hand, GraduationCap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { pickDefaultSchool } from "../lib/autoSelect";
import { localName, localFullName } from "../lib/localName";

const STATUSES = [
  { key: "present", color: "bg-emerald-600 text-white", icon: Check, label: "present" },
  { key: "absent", color: "bg-red-600 text-white", icon: X, label: "absent" },
  { key: "late", color: "bg-amber-500 text-white", icon: Clock, label: "late" },
  { key: "excused", color: "bg-sky-600 text-white", icon: Hand, label: "excused" },
];

function statusMeta(key) {
  return STATUSES.find((s) => s.key === key) || STATUSES[0];
}

export default function AttendancePage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canMark = can.markAttendance(user);
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  // Staff state
  const [schools, setSchools] = useState([]);
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [school, setSchool] = useState("");
  const [classId, setClassId] = useState("");

  // Personal state (parent / student)
  const [myChildren, setMyChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState("");
  const [history, setHistory] = useState([]);

  const today = new Date().toISOString().slice(0, 10);
  const monthStart = today.slice(0, 8) + "01";
  const [date, setDate] = useState(today);          // staff: single day
  const [dateFrom, setDateFrom] = useState(monthStart); // personal: range
  const [dateTo, setDateTo] = useState(today);
  const [marks, setMarks] = useState({});

  // ---------- Loaders ----------
  // Staff: load schools list (auto-select first)
  useEffect(() => {
    if (!isPersonal) api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => prev || pickDefaultSchool(list, user));
    });
  }, [isPersonal]);

  // Personal: load own/children data
  useEffect(() => {
    if (isParent) {
      api.get("/my/children").then((r) => {
        const kids = r.data || [];
        setMyChildren(kids);
        if (kids.length && !selectedChild) setSelectedChild(kids[0].id);
      });
    } else if (isStudent) {
      api.get("/my/student-record").then((r) => {
        const s = r.data;
        if (s) { setMyChildren([s]); setSelectedChild(s.id); }
      });
    }
  }, [isParent, isStudent]);

  // Staff classes (auto-select first)
  useEffect(() => {
    if (!isPersonal && school) api.get(`/classes?school_id=${school}`).then(r => {
      const list = r.data || [];
      setClasses(list);
      setClassId(prev => (prev && list.some(c => c.id === prev)) ? prev : (list[0]?.id || ""));
    });
  }, [school, isPersonal]);

  // Staff roster + marks
  useEffect(() => {
    if (isPersonal) return;
    if (classId) {
      api.get(`/students?class_id=${classId}`).then(r => setStudents(r.data || []));
      api.get(`/attendance?class_id=${classId}&date_from=${date}&date_to=${date}`).then(r => {
        const m = {}; (r.data || []).forEach(x => m[x.student_id] = x.status); setMarks(m);
      });
    }
  }, [classId, date, isPersonal]);

  // Personal: attendance history for selected child
  useEffect(() => {
    if (!isPersonal || !selectedChild) return;
    api.get(`/attendance?student_id=${selectedChild}&date_from=${dateFrom}&date_to=${dateTo}`)
      .then(r => setHistory((r.data || []).sort((a, b) => (b.date || "").localeCompare(a.date || ""))))
      .catch(() => setHistory([]));
  }, [isPersonal, selectedChild, dateFrom, dateTo]);

  const setStatus = (sid, status) => setMarks(prev => ({ ...prev, [sid]: status }));

  const submit = async () => {
    const records = students.map(s => ({ student_id: s.id, status: marks[s.id] || "present" }));
    await api.post("/attendance", { class_id: classId, date, records });
    toast.success(t("success"));
  };

  // ---------- Personal view (parent / student) ----------
  if (isPersonal) {
    const stats = history.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      acc.total += 1;
      return acc;
    }, { total: 0 });
    const presentRate = stats.total ? Math.round(((stats.present || 0) / stats.total) * 100) : 0;

    return (
      <div className="space-y-6">
        <div className="flex justify-between flex-wrap gap-3 items-center">
          <h1 className="text-3xl font-semibold">{t("rollCall")}</h1>
          <div className="flex gap-3 flex-wrap items-center">
            {isParent && myChildren.length > 1 ? (
              <Select value={selectedChild} onValueChange={setSelectedChild}>
                <SelectTrigger className="w-56" data-testid="att-child-select"><SelectValue placeholder={t("select_child")} /></SelectTrigger>
                <SelectContent>{myChildren.map(c => <SelectItem key={c.id} value={c.id}>{localFullName(c, lang)}</SelectItem>)}</SelectContent>
              </Select>
            ) : myChildren.length === 1 ? (
              <span className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md">
                {myChildren[0].full_name_ar || myChildren[0].full_name}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">{t("no_linked_children")}</span>
            )}
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="w-40" data-testid="att-date-from" />
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="w-40" data-testid="att-date-to" />
          </div>
        </div>

        {selectedChild && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Card className="p-4 border shadow-none">
              <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("present_rate") || "Present %"}</div>
              <div className="text-3xl font-semibold text-emerald-600 mt-1" data-testid="att-present-rate">
                {stats.total === 0 ? "—" : `${presentRate}%`}
              </div>
            </Card>
            {STATUSES.map((st) => (
              <Card key={st.key} className="p-4 border shadow-none">
                <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t(st.label)}</div>
                <div className="text-3xl font-semibold mt-1" data-testid={`att-count-${st.key}`}>{stats[st.key] || 0}</div>
              </Card>
            ))}
          </div>
        )}

        {selectedChild && (
          <>
            {/* Desktop: table */}
            <Card className="border shadow-none overflow-hidden hidden md:block">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("date") || "Date"}</th>
                    <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("status") || "Status"}</th>
                    <th className="text-start px-4 py-3 text-xs uppercase tracking-wider text-muted-foreground">{t("notes") || "Notes"}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((r, i) => {
                    const meta = statusMeta(r.status);
                    const Icon = meta.icon;
                    return (
                      <tr key={r.id || `${r.date}-${i}`} className={`border-t border-border ${i % 2 ? "bg-muted/20" : ""}`} data-testid={`att-hist-${i}`}>
                        <td className="px-4 py-3 font-mono">{r.date}</td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium ${meta.color}`}>
                            <Icon className="w-3.5 h-3.5" />{t(meta.label)}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{r.note || "—"}</td>
                      </tr>
                    );
                  })}
                  {history.length === 0 && (
                    <tr><td colSpan={3} className="px-4 py-12 text-center text-muted-foreground">{t("no_data")}</td></tr>
                  )}
                </tbody>
              </table>
            </Card>

            {/* Mobile: stacked cards */}
            <div className="md:hidden space-y-2.5">
              {history.map((r, i) => {
                const meta = statusMeta(r.status);
                const Icon = meta.icon;
                return (
                  <Card key={r.id || `${r.date}-${i}`} data-testid={`att-hist-card-${i}`}
                        className="p-3 border border-border/60 rounded-2xl shadow-sm flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-medium">{r.date}</div>
                      {r.note && <div className="text-xs text-muted-foreground truncate mt-0.5">{r.note}</div>}
                    </div>
                    <span className={`shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${meta.color}`}>
                      <Icon className="w-3.5 h-3.5" />{t(meta.label)}
                    </span>
                  </Card>
                );
              })}
              {history.length === 0 && <Card className="p-10 text-center text-muted-foreground border-dashed rounded-2xl">{t("no_data")}</Card>}
            </div>
          </>
        )}
      </div>
    );
  }

  // ---------- Staff view ----------
  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("rollCall")}</h1>
        <div className="flex gap-3 flex-wrap">
          <Select value={school} onValueChange={setSchool}>
            <SelectTrigger className="w-48"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
            <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger className="w-48" data-testid="att-class-select"><SelectValue placeholder={t("select_class")} /></SelectTrigger>
            <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="w-44" data-testid="att-date" />
          {canMark && <Button onClick={submit} disabled={!classId || students.length === 0} data-testid="att-submit">{t("submit_attendance")}</Button>}
        </div>
      </div>

      {classId && students.length > 0 ? (
        <div className="space-y-2">
          {students.map(s => (
            <Card key={s.id} className="p-3 border shadow-none flex items-center justify-between gap-4" data-testid={`att-row-${s.id}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary grid place-items-center"><GraduationCap className="w-5 h-5" /></div>
                <div className="font-medium">{localFullName(s, lang)}</div>
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                {STATUSES.map(st => {
                  const active = marks[s.id] === st.key;
                  const Icon = st.icon;
                  return (
                    <button
                      key={st.key}
                      onClick={() => canMark && setStatus(s.id, st.key)}
                      data-testid={`att-${st.key}-${s.id}`}
                      disabled={!canMark}
                      className={`px-3 py-2 rounded-md text-xs font-medium smooth flex items-center gap-1 border ${active ? st.color + " border-transparent" : "bg-card text-foreground border-border hover:bg-muted"} ${!canMark ? "opacity-60 cursor-not-allowed" : ""}`}>
                      <Icon className="w-3.5 h-3.5" /> {t(st.label)}
                    </button>
                  );
                })}
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-8 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>
      )}
    </div>
  );
}
